import { logout, refresh, verify } from '@/api/security';
import jwt_decode from 'jwt-decode';

var timer;

// @/router se importa de forma diferida dentro de la función para evitar
// que el side-effect `Vue.use(VueRouter)` del router se dispare con el
// solo hecho de cargar auth.js. Sin esto, cualquier módulo que importa
// auth.js (transitivo a través de api.js) introduce VueRouter en árboles
// de tests que mockean $route via `mocks: { $route }` — vue-test-utils
// pierde la pelea contra el mixin global de vue-router y el mock falla.
// La redirección se mantiene en runtime con el mismo comportamiento SPA.
function redirectToLogin() {
    return import('@/router').then(({ default: router }) => {
        router.push('/');
    });
}

export function getTokenSession(session){
    try {
        return jwt_decode(session.token);
    } catch (err) {
        clearLocalStorage();
        // Retornamos la promise del redirect en el path de error para que
        // callers `await` sincronicen con el push del router. El happy path
        // sigue devolviendo el token decodificado — signature compatible.
        return redirectToLogin();
    }
}

export function getSession () {
    return JSON.parse(localStorage.getItem('session'));
}

// ¿Hay sesión y su token TODAVÍA sirve? Replica la aritmética de expiración
// del interceptor de api.js (exp del JWT ajustado por time_difference):
// llamar a la API con una sesión vencida dispara clearLocalStorage + redirect
// a "/" desde el interceptor, lo que rompería flujos que no son de la app
// (p.ej. el OAuth de /mcp/authorize perdería su login_challenge). Los callers
// chequean esto ANTES de disparar requests con sesión dudosa.
export function hasLiveSession() {
    const session = getSession();
    if (!session || !session.token) {
        return false;
    }
    try {
        const token = jwt_decode(session.token);
        const timeDiff = Number(localStorage.getItem('time_difference')) || 0;
        let expTime = new Date(0).setUTCSeconds(token.exp);
        expTime = timeDiff > 0 ? expTime - timeDiff : expTime + Math.abs(timeDiff);
        return expTime - new Date().getTime() > 0;
    } catch (err) {
        return false;
    }
}

export function clearLocalStorage() {
    stopTimer();
    localStorage.removeItem('email');
    localStorage.removeItem('session');
    localStorage.removeItem('time_difference');
    localStorage.removeItem('activeCompanyId');
}

export function checkTimer() {
    const session = getSession();
    if (session && session.token) {
        // get token
        let token = '';
        token = getTokenSession(session);
        // get time diff
        const timeDiff = Number(localStorage.getItem('time_difference'));
        // get expiration time
        let expDate = new Date(0);
        expDate.setUTCSeconds(token.exp);
        let expTime = expDate.getTime();
        if (timeDiff > 0) {
            expTime = expTime - timeDiff;
        } else {
            expTime = expTime + Math.abs(timeDiff);
        }
        // set countdown time
        const thirtySec = 30000;
        const cdTime = expTime - thirtySec;
        if (timer) {
            clearInterval(timer);
        }
        timer = setInterval(() => {
            // get local time
            const localDate = new Date();
            const localTime = localDate.getTime();
            const countdown = localTime + thirtySec;
            const diff = cdTime - countdown;
            if (diff <= 0) {
                logoutAndClean();
                redirectToLogin();
            }
        }, thirtySec);
    }
}

export function stopTimer() {
    if (timer) {
        clearInterval(timer);
    }
}

export function setSession(data) {
    localStorage.setItem('session', JSON.stringify(data));
    const payload = getTokenSession(data);
    let local = new Date();
    let server = new Date(0);
    server = server.setUTCSeconds(payload.orig_iat);
    local = local.getTime();
    const timeDiff = server - local;
    localStorage.setItem('time_difference', timeDiff.toString());
    localStorage.setItem('email', data.user.email);
    checkTimer();
}

export function logoutAndClean() {
    return logout().finally(() => {
        clearLocalStorage();
    })
}

export async function isLoggedIn() {
    const session = getSession();
    let tokenIsValid = false;
    if ( typeof session !== 'undefined' && session !== null ) {
        try {
            const res = await verify(session.token);
            tokenIsValid = res?.status === 200;
        } catch (error) {
            try {
                const res = await refresh(session.token);
                tokenIsValid = res?.status === 200;
                setSession(res.data);
            } catch (error) {
                // pass
            }
        }
        if ( !tokenIsValid ) clearLocalStorage();
            return tokenIsValid;
    }
}
